<template>
  <section class="suggestion-container">
    <section class="info-bar">
      <BarItem
        v-for="(item, index) in infoBarItems"
        :key="item.label"
        :trendInfo="item.trendInfo"
        componentType="info"
        :text="item.text"
        :label="$t(item.label)"
        :iconUrl="item.iconUrl"
        :isAmended="item.isAmended"
        :class="{ last: index === infoBarItems.length - 1, first: index === 0}"
      />
    </section>
    <section class="suggestion-table-container">
      <Loading v-if="loading" />
      <SuggestionTable v-else :topIssues="tableData" />
    </section>
  </section>
</template>

<script>
import SuggestionTable from "../components/SuggestionTable";
import BarItem from "../components/BarItem";
import Loading from "@/modules/common/components/Loading";
import { mapGetters } from "vuex";
import {
  getTopIssues,
  getOverviewSums,
  getAnalyticsSums,
} from "../services/performanceService";
import utilService from "../../common/services/utilService";
import { infoBarItemsExcept, trendArrowColors } from "../consts/consts";

export default {
  props:{
    hideInfo: { type: Array, required: false}
  },
  data() {
    return {
      tableData: { weekIssues: {}, monthIssues: {} },
      loading: false,
      infoBarItems: JSON.parse(JSON.stringify(infoBarItemsExcept('unnecessary_arrivals'))),
    };
  },
  async created() {
    this.assignTopBarItems();
    this.updatePageData();
    this.$store.watch(
      (state) => this.isRTL,
      (newValue) => {
        this.updatePageData();
      },
      { deep: true }
    );
    this.$store.watch(
      (state) => this.overviewData,
      (newValue) => {
        this.assignTopBarItems();
      },
      { deep: true }
    );
    this.$store.watch(
      (state) => this.analyticsData,
      (newValue) => {
        this.assignTopBarItems();
      },
      { deep: true }
    );
  },
  methods: {
    async updatePageData() {
      this.loading = true;
      let monthTime = { timeFrom: new Date(), timeTo: new Date() };
      monthTime.timeFrom.setMonth(monthTime.timeFrom.getMonth() - 1);
      monthTime = this.yyyymmdd(monthTime);
      const monthTopIssuesPrm = getTopIssues({
        ...this.filterSelected,
        time: { ...monthTime },
      });
      let weekTime = { timeFrom: new Date(), timeTo: new Date() };
      weekTime.timeFrom.setDate(weekTime.timeFrom.getDate() - 7);
      weekTime = this.yyyymmdd(weekTime);
      const weekTopIssuesPrm = getTopIssues({
        ...this.filterSelected,
        time: { ...weekTime },
      });
      const [weekTopIssues, monthTopIssues] = await Promise.all([
        weekTopIssuesPrm,
        monthTopIssuesPrm,
      ]);
      this.tableData.weekIssues = weekTopIssues;
      this.tableData.monthIssues = monthTopIssues;
      this.loading = false;
    },
    yyyymmdd(time) {
      time.timeFrom = time.timeFrom.toISOString().split("T")[0];
      time.timeTo = time.timeTo.toISOString().split("T")[0];
      return time;
    },
    async assignTopBarItems() {
      if (!this.overviewData || !this.analyticsData) return;
      const overviewSums = getOverviewSums(this.overviewData);
      const analyticsSums = getAnalyticsSums(this.analyticsData.hourData);
      const trendInfo = this.trendInfo || await this.$store.dispatch('computeTrendInfo', { filter: this.filterSelected });
      this.infoBarItems = this.infoBarItems.reduce((acc, item) => {
        if (analyticsSums[item.name] || overviewSums[item.name]) {
          item.trendInfo = trendInfo[item.name];
          item.trendInfo
            ? (item.trendInfo.color =
                trendArrowColors[item.name][item.trendInfo.upDown])
            : item.trendInfo;
          if (item.name === "visits") item.isAmended = analyticsSums.isAmended || overviewSums.isAmended
          if (item.name === "avg_score" || item.name === "response_time_SLA") {
            if (item.name === "avg_score") {
              item.text = utilService.kFormatter(overviewSums[item.name]);
            } else {
              item.text = utilService.kMinutesToHours(overviewSums[item.name]);
            }
          } else
            item.text = utilService.kFormatter(
              analyticsSums[item.name] || overviewSums[item.name]
            );
          try {
            if (item.name === "response_time_SLA")
              item.text += ` ${this.$t("min")}`;
          } catch (err) {}
          acc.push(item);
        }
        return acc;
      }, []);
      if(this.hideInfo && this.hideInfo.length > 0) this.infoBarItems = this.infoBarItems.filter(col => this.hideInfo.indexOf(col.name) === -1);
    },
  },
  computed: mapGetters([
    "analyticsData",
    "overviewData",
    "isRTL",
    "filterSelected",
    "trendInfo",
  ]),
  components: { SuggestionTable, Loading, BarItem },
};
</script>

<style lang="scss" scoped>
.suggestion-container {
  width: 100%;
  max-width: 100%;
  padding: 0 16px;
  
  @media (min-width: 768px) {
    padding: 0 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 32px;
  }
}

.info-bar {
  // Grid (not flex) guarantees every card gets the exact same column width,
  // regardless of how much text/content any single card holds.
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(150px, 1fr);
  align-items: stretch;
  gap: 12px;
  margin-bottom: 2vh;
  overflow-x: auto;
  padding-bottom: 4px;
  padding-top: 4px;
  scrollbar-width: thin;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
    
    &:hover {
      background: #a8a8a8;
    }
  }

  > * {
    min-width: 0;
  }
}

.suggestion-table-container {
  width: 100%;
  min-height: 200px;
  
  @media (max-width: 640px) {
    margin-top: 16px;
  }
}
</style>